import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1;
const EPSILON = 0.001;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();

  /** @type {number} */
  #scale = DEFAULT_ZOOM;

  /** @type {number} */
  #initialDistance = 0;

  /** @type {number} */
  #startScale = DEFAULT_ZOOM;

  /** @type {Point} */
  #translate = { x: 0, y: 0 };

  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };

  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };

  /** @type {boolean} */
  #isDragging = false;

  /** @type {boolean} */
  #initialized = false;

  /** @type {boolean} */
  #dialogListenersInitialized = false;

  /** @type {number | null} */
  #animationFrame = null;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (this.#controller.signal.aborted) {
      this.#controller = new AbortController();
      this.#initialized = false;
      this.#dialogListenersInitialized = false;
    }

    if (!this.#image) return;

    this.#applyBasePaintStyles();
    this.#setDefaultTransformVariables();
    this.#initResizeListener();
    this.#initDialogListeners();

    if (!isMobileBreakpoint()) {
      this.#resetZoom();
      return;
    }

    this.#initEventListeners();
    this.#updateTransform();
  }

  disconnectedCallback() {
    this.#resetZoom();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  #initDialogListeners() {
    if (this.#dialogListenersInitialized) return;

    const dialog = this.closest('dialog');
    if (!dialog) return;

    this.#dialogListenersInitialized = true;

    const { signal } = this.#controller;

    dialog.addEventListener('close', this.#handleDialogClose, { signal });
    dialog.addEventListener('cancel', this.#handleDialogClose, { signal });
  }

  #initEventListeners() {
    if (this.#initialized) return;

    this.#initialized = true;

    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);
    this.addEventListener('touchcancel', this.#handleTouchCancel, options);

    this.#image?.addEventListener('load', this.#handleImageLoad, { signal });
  }

  #handleResize = () => {
    const isMobile = isMobileBreakpoint();

    this.#applyBasePaintStyles();

    if (!isMobile) {
      this.#resetZoom();
      return;
    }

    if (!this.#initialized) {
      this.#initEventListeners();
    }

    this.#requestUpdateTransform();
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  #handleDialogClose = () => {
    this.#resetZoom();

    requestAnimationFrame(() => {
      this.#resetZoom();
    });
  };

  #handleImageLoad = () => {
    this.#resetZoom();

    if (isMobileBreakpoint()) {
      this.#requestUpdateTransform();
    }
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    if (event.touches.length === 2) {
      preventDefault(event);

      const [point1, point2] = Array.from(event.touches).map(touchToPoint);
      if (!point1 || !point2) return;

      this.#startZoomGesture(point1, point2);
      return;
    }

    if (event.touches.length === 1) {
      const point = touchToPoint(event.touches[0]);
      if (!point) return;

      this.#startDragGesture(point);

      if (this.#scale > MIN_ZOOM + EPSILON) {
        preventDefault(event);
      }
    }
  };

  /**
   * @param {Point} point1
   * @param {Point} point2
   */
  #startZoomGesture(point1, point2) {
    this.#initialDistance = getDistance(point1, point2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  /**
   * @param {Point} point
   */
  #startDragGesture(point) {
    this.#startPosition = { x: point.x, y: point.y };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = this.#scale > MIN_ZOOM + EPSILON;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    if (event.touches.length === 2) {
      preventDefault(event);
      this.#processZoomGesture(event);
      return;
    }

    if (event.touches.length === 1 && this.#isDragging) {
      preventDefault(event);
      this.#processDragGesture(event);
    }
  };

  /**
   * @param {TouchEvent} event
   */
  #processZoomGesture(event) {
    const [point1, point2] = Array.from(event.touches).map(touchToPoint);
    if (!point1 || !point2 || !this.#initialDistance) return;

    const currentMidpoint = getMidpoint(point1, point2);
    const currentDistance = getDistance(point1, point2);
    const oldScale = this.#scale || DEFAULT_ZOOM;

    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);

    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    const distanceFromCenter = {
      x: currentMidpoint.x - containerCenter.x,
      y: currentMidpoint.y - containerCenter.y,
    };

    const scaleDelta = this.#scale / oldScale - 1;

    this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;

    this.#isDragging = false;
    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #processDragGesture(event) {
    const point = touchToPoint(event.touches[0]);
    if (!point) return;

    this.#translate = {
      x: this.#startTranslate.x + (point.x - this.#startPosition.x) / this.#scale,
      y: this.#startTranslate.y + (point.y - this.#startPosition.y) / this.#scale,
    };

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 1) {
      const point = touchToPoint(event.touches[0]);
      if (!point) return;

      this.#startPosition = { x: point.x, y: point.y };
      this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
      this.#startScale = this.#scale;
      this.#initialDistance = 0;
      this.#isDragging = this.#scale > MIN_ZOOM + EPSILON;
      return;
    }

    if (event.touches.length === 0) {
      this.#isDragging = false;

      if (this.#scale <= MIN_ZOOM + EPSILON) {
        this.#scale = MIN_ZOOM;
        this.#translate = { x: 0, y: 0 };
      }

      this.#requestUpdateTransform();
    }
  };

  #handleTouchCancel = () => {
    this.#isDragging = false;

    if (this.#scale <= MIN_ZOOM + EPSILON) {
      this.#resetZoom();
      return;
    }

    this.#requestUpdateTransform();
  };

  /**
   * Zoom mínimo 1:
   * la imagen abre completa tipo contain, sin volver al cover original.
   *
   * @returns {number | null}
   */
  #getMinZoom = () => {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    const imageDimensions = this.#getImageDimensions();

    if (!imageDimensions || !containerWidth || !containerHeight) return null;

    return MIN_ZOOM;
  };

  /**
   * @returns {{ width: number, height: number } | null}
   */
  #getImageDimensions = () => {
    const image = this.#image;
    if (!image) return null;

    const containerRect = this.getBoundingClientRect();
    const { naturalWidth, naturalHeight } = image;
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;

    if (!naturalWidth || !naturalHeight || !containerWidth || !containerHeight) return null;
    if (!containerRect.width || !containerRect.height) return null;

    const containerAspect = containerRect.width / containerRect.height;
    const naturalAspect = naturalWidth / naturalHeight;

    let imageWidth;
    let imageHeight;

    if (naturalAspect > containerAspect) {
      imageWidth = containerRect.width;
      imageHeight = imageWidth / naturalAspect;
    } else {
      imageHeight = containerRect.height;
      imageWidth = imageHeight * naturalAspect;
    }

    if (!imageWidth || !imageHeight) return null;

    return {
      width: imageWidth,
      height: imageHeight,
    };
  };

  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;

    if (!containerWidth || !containerHeight) return;

    const minZoom = this.#getMinZoom();
    if (!minZoom) return;

    this.#scale = clamp(this.#scale, minZoom, MAX_ZOOM);

    const imageDimensions = this.#getImageDimensions();
    if (!imageDimensions) return;

    const { width: imageWidth, height: imageHeight } = imageDimensions;

    const scaledWidth = imageWidth * this.#scale;
    const scaledHeight = imageHeight * this.#scale;

    const excessWidth = Math.max(0, scaledWidth - containerWidth);
    const excessHeight = Math.max(0, scaledHeight - containerHeight);

    if (excessWidth <= EPSILON) {
      this.#translate.x = 0;
    } else {
      const maxTranslateX = excessWidth / (2 * this.#scale);
      this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    }

    if (excessHeight <= EPSILON) {
      this.#translate.y = 0;
    } else {
      const maxTranslateY = excessHeight / (2 * this.#scale);
      this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);
    }

    if (this.#scale <= minZoom + EPSILON) {
      this.#scale = minZoom;
      this.#translate.x = 0;
      this.#translate.y = 0;
    }
  }

  #requestUpdateTransform = () => {
    if (this.#animationFrame) return;

    this.#animationFrame = requestAnimationFrame(this.#updateTransform);
  };

  #cancelAnimationFrame() {
    if (!this.#animationFrame) return;

    cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = null;
  }

  #applyBasePaintStyles() {
    this.style.overflow = 'hidden';
    this.style.backgroundColor = '#ffffff';
    this.style.overscrollBehavior = 'none';
    this.style.touchAction = isMobileBreakpoint() ? 'pan-x' : 'auto';

    const image = this.#image;
    if (!image) return;

    image.style.backgroundColor = '#ffffff';
    image.style.transformOrigin = 'center center';
    image.style.backfaceVisibility = 'hidden';
  }

  #setDefaultTransformVariables() {
    this.style.setProperty('--drag-zoom-scale', DEFAULT_ZOOM.toString());
    this.style.setProperty('--drag-zoom-translate-x', '0px');
    this.style.setProperty('--drag-zoom-translate-y', '0px');

    this.removeAttribute('data-drag-zoom-active');
    this.style.touchAction = isMobileBreakpoint() ? 'pan-x' : 'auto';

    const image = this.#image;
    if (image) {
      image.style.removeProperty('will-change');
      image.style.removeProperty('transform');
    }
  }

  #setActiveTransformState() {
    const image = this.#image;
    const isActive = this.#scale > MIN_ZOOM + EPSILON;

    if (isActive) {
      this.setAttribute('data-drag-zoom-active', 'true');
      this.style.touchAction = 'none';

      if (image) {
        image.style.willChange = 'transform';
      }

      return;
    }

    this.removeAttribute('data-drag-zoom-active');
    this.style.touchAction = isMobileBreakpoint() ? 'pan-x' : 'auto';
    this.#translate.x = 0;
    this.#translate.y = 0;

    if (image) {
      image.style.removeProperty('will-change');
      image.style.removeProperty('transform');
    }
  }

  #resetZoom = () => {
    this.#cancelAnimationFrame();

    this.#scale = DEFAULT_ZOOM;
    this.#initialDistance = 0;
    this.#startScale = DEFAULT_ZOOM;
    this.#translate = { x: 0, y: 0 };
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;

    this.#applyBasePaintStyles();
    this.#setDefaultTransformVariables();
  };

  #updateTransform = () => {
    this.#animationFrame = null;

    this.#constrainTranslation();
    this.#setActiveTransformState();

    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  };

  destroy() {
    this.#resetZoom();
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @param {Touch | undefined} touch
 * @returns {Point | undefined}
 */
function touchToPoint(touch) {
  if (!touch) return undefined;

  return {
    x: touch.clientX,
    y: touch.clientY,
  };
}

/**
 * @param {Point} point1
 * @param {Point} point2
 * @returns {number}
 */
function getDistance(point1, point2) {
  const dx = point1.x - point2.x;
  const dy = point1.y - point2.y;

  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * @param {Point} point1
 * @param {Point} point2
 * @returns {Point}
 */
function getMidpoint(point1, point2) {
  return {
    x: (point1.x + point2.x) / 2,
    y: (point1.y + point2.y) / 2,
  };
}